import { useEffect, createElement } from "react";
import { toast } from "sonner";
import { CircleArrowLeft } from "lucide-react";
import { getLoadId } from "@/utils/load";

// Shared ref for previous offers state
const prevQuotesRef = { current: [] };
const prevBookingsRef = { current: [] };

export const updatePrevOffers = (quotes, bookings) => {
  prevQuotesRef.current = quotes || [];
  prevBookingsRef.current = bookings || [];
};

export const useOfferStatusNotifications = (quotes, bookings) => {
  useEffect(() => {
    const prevQuotes = prevQuotesRef.current;
    const currentQuotes = quotes || [];

    if (prevQuotes.length === 0) {
      prevQuotesRef.current = currentQuotes;
      return;
    }

    const prevQuotesMap = new Map(prevQuotes.map((q) => [q.id, q]));

    currentQuotes.forEach((quote) => {
      const prevQuote = prevQuotesMap.get(quote.id);
      if (prevQuote && prevQuote.status !== "ACCEPTED" && quote.status === "ACCEPTED") {
        const loadId = getLoadId(quote.loadBoardItem.uniqueId);
        toast.success(`Broker has accepted Load ${loadId} offer!`, { className: "cm-toast-success", toasterId: "top-right" });
      }
    });

    // Check for newly declined quotes (exclude CANCELLED and EXPIRED — neither is a broker decision)
    currentQuotes.forEach((quote) => {
      const prevQuote = prevQuotesMap.get(quote.id);
      if (prevQuote && prevQuote.status !== "DECLINED" && quote.status === "DECLINED") {
        const loadId = getLoadId(quote.loadBoardItem.uniqueId);
        toast.error(`Broker has declined Load ${loadId} offer!`, { className: "cm-toast-error", toasterId: "top-right" });
      }
    });

    // Check for newly countered quotes (broker sent a counter offer)
    currentQuotes.forEach((quote) => {
      const prevQuote = prevQuotesMap.get(quote.id);
      if (prevQuote && prevQuote.status !== "COUNTERED" && quote.status === "COUNTERED") {
        const loadId = getLoadId(quote.loadBoardItem.uniqueId);
        toast.warning(`Broker has sent counter bid for Load ${loadId}`, {
          className: "cm-toast-warning",
          toasterId: "top-right",
          icon: createElement(CircleArrowLeft, { className: "size-5" }),
        });
      }
    });

    prevQuotesRef.current = currentQuotes;
  }, [quotes]);

  useEffect(() => {
    const prevBookings = prevBookingsRef.current;
    const currentBookings = bookings || [];

    if (prevBookings.length === 0) {
      prevBookingsRef.current = currentBookings;
      return;
    }

    const prevBookingsMap = new Map(prevBookings.map((b) => [b.id, b]));

    // Check for newly accepted bookings
    currentBookings.forEach((booking) => {
      const prevBooking = prevBookingsMap.get(booking.id);
      if (prevBooking?.status !== "ACCEPTED" && booking.status === "ACCEPTED") {
        const loadId = getLoadId(booking.loadBoardItem.uniqueId);
        toast.success(`Broker has accepted Load ${loadId} offer!`, { className: "cm-toast-success", toasterId: "top-right" });
      }
    });

    // Check for newly declined bookings (exclude CANCELLED as it's user-initiated)
    currentBookings.forEach((booking) => {
      const prevBooking = prevBookingsMap.get(booking.id);
      if (prevBooking?.status !== "DECLINED" && booking.status === "DECLINED") {
        const loadId = getLoadId(booking.loadBoardItem.uniqueId);
        toast.error(`Broker has declined Load ${loadId} offer!`, { className: "cm-toast-error", toasterId: "top-right" });
      }
    });

    prevBookingsRef.current = currentBookings;
  }, [bookings]);
};
